import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import DeviceService from '../../../../services/device.service';
import Dropdown from '../../../common/dropdown';
import { useTranslation } from 'react-i18next';
import MultiSelect from '../../../common/MultiSelect';
import FleetsService from '../../../../services/fleets.services';
import { MultiSelectOption } from '../../../../models/MultiSelectOption';
import { DropdownOption } from '../../../../models/DropdownOption';
import AssetModal from './asset.modal';
import { toastSuccess } from '../../../../App';
import DeleteDeviceModal from './DeleteDeviceModal';
// import ConfirmationModal from "../../../common/ConfirmationModal";

// TODO: store this in a config file
const baseAssetUrl = 'https://operator-uploads.s3.us-east-2.amazonaws.com';

const compressorRefrigerants = [
  { value: 'R-22', label: 'R-22' },
  { value: 'R-410A', label: 'R-410A' },
  { value: 'R-134a', label: 'R-134a' },
  { value: 'R-404A', label: 'R-404A' },
  { value: 'R-507A', label: 'R-507A' },
  { value: 'R-407A', label: 'R-407A' },
  { value: 'R-407C', label: 'R-407C' },
  { value: 'R-427A', label: 'R-427A' },
  { value: 'R-422B', label: 'R-422B' },
  { value: 'R-422D', label: 'R-422D' },
  { value: 'R-407F', label: 'R-407F' },
  { value: 'R-448A', label: 'R-448A' },
  { value: 'R-421A', label: 'R-421A' },
  { value: 'R-513A', label: 'R-513A' },
  { value: 'R-449A', label: 'R-449A' },
  { value: 'R-452A', label: 'R-452A' },
  { value: 'Unknown', label: 'Unknown' },
];

export default function SummitCompressorDescription() {
  const { t } = useTranslation();
  const { siteId, deviceId } = useParams();
  const navigate = useNavigate();
  const [fleets, setFleets] = useState<MultiSelectOption[]>([]);
  const [device, setDevice] = useState<any>();
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [compressorType, setCompressorType] = useState<DropdownOption<string>>();
  const [horsepower, setHorsepower] = useState<string>('');
  const [refrigerant, setRefrigerant] = useState<DropdownOption<string>>();
  const [name, setName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<MultiSelectOption[]>([]);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const canSave =
    name?.trim() !== '' &&
    serialNumber.trim() !== '' &&
    compressorType?.value.trim() !== '' &&
    horsepower.trim() !== '' &&
    refrigerant?.value.trim() !== '';

  const compressorTypes = [
    { value: 'scroll', label: t('sites.scroll') },
    { value: 'reciprocating', label: t('sites.reciprocating') },
    { value: 'screw', label: t('sites.screw') },
  ];

  const handleSave = () => {
    if (siteId && deviceId) {
      const updatedData: any = {
        name,
        notes,
        type: device.type,
        subject: {
          serialNumber,
          compressorType: compressorType?.value,
          horsepower: horsepower,
          refrigerant: refrigerant?.value,
        },
        fleets: selectedOptions.map(
          (option: MultiSelectOption) => option.value,
        ),
      };

      DeviceService.updateDevice(siteId, deviceId, updatedData).then(
        (response) => {
          response.json().then((data) => {
            setDevice(data);
            toastSuccess(t('sites.deviceUpdated'));
          });
        },
      );
    }
  };

  const fetchData = async () => {
    if (siteId && deviceId) {
      const deviceResponse = await DeviceService.getDevice(siteId, deviceId);
      const data = await deviceResponse.json();
      setDevice(data);
      setName(data.name);
      setNotes(data.notes);
      setSerialNumber(data.subject.serialNumber);
      setCompressorType(
        compressorTypes.find(
          (type) => type.value === data.subject.compressorType,
        ),
      );
      setHorsepower(data.subject.horsepower);
      setRefrigerant(
        compressorRefrigerants.find(
          (ref) => ref.value === data.subject.refrigerant,
        ),
      );
      setSelectedOptions(
        data.fleets.map((fleet: any) => ({
          value: fleet._id,
          label: fleet.name,
        })),
      );
    }
  };

  const fetchFleets = async () => {
    const response = await FleetsService.getFleets();
    const data = await response.json();

    const fleets = data.map((fleet: any) => ({
      value: fleet._id,
      label: fleet.name,
    }));

    setFleets(fleets);
  };

  const handleDelete = async () => {
    if (siteId && deviceId) {
      try {
        const response = await DeviceService.deleteDevice(siteId, deviceId);
        if (response.ok) {
          toastSuccess(t('sites.deviceDeleted'));
          navigate(`/sites/${siteId}`);
        }
      } catch (error) {
        console.error('Failed to delete device:', error);
      }
    }
  };

  useEffect(() => {
    fetchData();
    fetchFleets();
  }, []);

  const handleHorsepowerChange = (text: string) => {
    let cleanText = text.replace(/[^0-9\.]/g, '');

    if (cleanText.includes('.')) {
      const parts = cleanText.split('.');
      cleanText = parts[0] + '.' + (parts[1] ? parts[1].substring(0, 1) : '');
    }

    if (cleanText === '' || parseFloat(cleanText) % 0.5 === 0) {
      setHorsepower(cleanText);
    }
  };

  return (
    <>
      <div className="flex flex-row items-center justify-between pt-3">
        <h2 className="text-lg font-bold leading-6 text-gray-900">
          {t('sites.device')}
        </h2>

        <div>
          <button
            type="button"
            className={`rounded-md border border-transparent bg-indigo-600 hover:bg-indigo-700 py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm mr-2`}
            onClick={() => setIsAssetModalOpen(true)}
          >
            View Images
          </button>
          <button
            type="button"
            className={`rounded-md border border-transparent ${canSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-200'
              } py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm mr-2`}
            onClick={handleSave}
            disabled={!canSave}
          >
            {t('save')}
          </button>
          <button
            type="button"
            className="rounded-md border border-transparent bg-red-600 hover:bg-red-700 py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            {t('sites.deleteDevice')}
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-6 gap-6">
        <div className="col-span-3 sm:col-span-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            {t('sites.name')}
          </label>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          />
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            {t('sites.notes')}
          </label>

          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-6 gap-6">
        <div className="col-span-4 sm:col-span-2">
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            {t('sites.serialNumber')}
          </label>

          <input
            type="text"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          />
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label
            htmlFor="name"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {t('sites.compressorType')}
          </label>

          <Dropdown
            options={compressorTypes}
            onChange={setCompressorType}
            selectedOption={compressorType}
          />
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label
            htmlFor="name"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {t('sites.horsepower')}
          </label>
          <input
            type="number"
            step={0.5}
            value={horsepower}
            onChange={(e) => handleHorsepowerChange(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
          />
        </div>

        <div className="col-span-2 sm:col-span-1">
          <label
            htmlFor="name"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            {t('sites.refrigerant')}
          </label>

          <Dropdown
            options={compressorRefrigerants}
            onChange={setRefrigerant}
            selectedOption={refrigerant}
          />
        </div>
      </div>

      <div className="mt-6 flex flex-row col-span-12 sm:col-span-6 mb-6 grid grid-cols-6">
        <div className="col-span-3">
          <MultiSelect
            title={t('fleets.title')}
            options={fleets}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
          />
        </div>
      </div>

      <AssetModal
        imagePaths={device?.attachments || []}
        baseUrl={baseAssetUrl}
        isOpen={isAssetModalOpen}
        setOpen={setIsAssetModalOpen}
      />

      <DeleteDeviceModal
        isOpen={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        onConfirm={handleDelete}
        deviceName={name}
      />
    </>
  );
}
